<template>
  <div class="project-creation">
    <!-- Header -->
    <header class="creation-header">
      <div class="header-content">
        <div class="logo-section">
          <div class="kpmg-logo" @click="navigateToHome">
            <img src="/kpmg-logo-blue.svg" alt="KPMG" class="kpmg-logo-img" />
          </div>
          <div class="breadcrumb">
            <span @click="goBack" class="breadcrumb-link">Dashboard</span>
            <span class="breadcrumb-separator">></span>
            <span class="breadcrumb-current">{{ viewMode ? 'View Project' : (isEditMode ? 'Edit Project' : 'Create New Project') }}</span>
          </div>
        </div>
        <div class="header-actions" v-if="!viewMode">
          <button @click="saveDraft" class="save-draft-btn" :disabled="!hasChanges">Save Draft</button>
          <button @click="goBack" class="cancel-btn">Cancel</button>
        </div>
        <div class="header-actions" v-else>
          <button @click="goBack" class="cancel-btn">← Back to Projects</button>
        </div>
      </div>
    </header>

    <!-- Progress Tabs -->
    <section class="tabs-section">
      <div class="container">
        <div class="tabs-container">
          <div class="tabs-header">
            <h1>{{ viewMode ? 'View Project Assessment' : (isEditMode ? 'Edit Project Assessment' : 'New Project Assessment') }}</h1>
            <p>Complete the 9-dimension assessment framework for comprehensive project evaluation</p>
          </div>
          
          <div class="tabs-navigation">
            <div 
              v-for="(tab, index) in tabs" 
              :key="tab.id"
              :class="['tab-item', { 
                'active': currentTab === tab.id, 
                'completed': tab.completed,
                'accessible': tab.accessible 
              }]"
              @click="selectTab(tab.id)"
            >
              <div class="tab-number">{{ index + 1 }}</div>
              <div class="tab-content">
                <h3>{{ tab.name }}</h3>
                <p>{{ tab.description }}</p>
              </div>
              <div class="tab-status">
                <div v-if="tab.completed" class="status-icon completed">✓</div>
                <div v-else-if="tab.hasData" class="status-icon partial">●</div>
                <div v-else class="status-icon empty">○</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Tab Content -->
    <section class="content-section">
      <div class="container">
        <div class="content-container">
          
          <!-- Basic Project Data Tab -->
          <div v-if="currentTab === 'basic-data'" class="tab-content">
            <BasicProjectDataTab 
              :projectData="projectData.basicData"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

          <!-- Strategic Suitability Tab -->
          <div v-if="currentTab === 'strategic-suitability'" class="tab-content">
            <StrategicSuitabilityTab 
              :projectData="projectData.strategicSuitability"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

          <!-- Preliminary Feasibility Tab -->
          <div v-if="currentTab === 'preliminary-feasibility'" class="tab-content">
            <PreliminaryFeasibilityTab 
              :projectData="projectData.preliminaryFeasibility"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

          <!-- Risk Assessment Tab -->
          <div v-if="currentTab === 'risk-assessment'" class="tab-content">
            <RiskAssessmentTab 
              :projectData="projectData.riskAssessment"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

          <!-- PPP Suitability Tab -->
          <div v-if="currentTab === 'ppp-suitability'" class="tab-content">
            <PPPSuitabilityTab 
              :projectData="projectData.pppSuitability"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

          <!-- Fiscal Affordability Tab -->
          <div v-if="currentTab === 'fiscal-affordability'" class="tab-content">
            <FiscalAffordabilityTab 
              :projectData="projectData.fiscalAffordability"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

          <!-- Institutional Capability Tab -->
          <div v-if="currentTab === 'institutional-capability'" class="tab-content">
            <InstitutionalCapabilityTab 
              :projectData="projectData.institutionalCapability"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

          <!-- Climate Risk Exposure Tab -->
          <div v-if="currentTab === 'climate-risk'" class="tab-content">
            <ClimateRiskTab 
              :projectData="projectData.climateRisk"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

          <!-- Climate-Smart PPP Tab -->
          <div v-if="currentTab === 'climate-smart-ppp'" class="tab-content">
            <ClimateSmartPPPTab 
              :projectData="projectData.climateSmartPPP"
              :viewMode="viewMode"
              @update="updateTabData"
              @validate="validateTab"
            />
          </div>

        </div>

        <!-- Navigation Footer -->
        <div class="navigation-footer" v-if="!viewMode">
          <button 
            @click="previousTab" 
            :disabled="!canGoPrevious"
            class="nav-button secondary"
          >
            ← Previous
          </button>
          
          <div class="tab-progress">
            <span>{{ currentTabIndex + 1 }} of {{ tabs.length }}</span>
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :style="{ width: progressPercentage + '%' }"
              ></div>
            </div>
          </div>
          
          <button 
            v-if="!isLastTab"
            @click="nextTab" 
            :disabled="!canGoNext"
            class="nav-button primary"
          >
            Next →
          </button>
          
          <button 
            v-else
            @click="completeAssessment" 
            :disabled="!canComplete"
            class="nav-button primary complete"
          >
            {{ isEditMode ? 'Update Assessment' : 'Complete Assessment' }}
          </button>
        </div>
        
        <!-- View Mode Footer -->
        <div class="navigation-footer" v-else>
          <div></div>
          <div class="tab-progress">
            <span>{{ currentTabIndex + 1 }} of {{ tabs.length }}</span>
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :style="{ width: progressPercentage + '%' }"
              ></div>
            </div>
          </div>
          <div></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import BasicProjectDataTab from './tabs/BasicProjectDataTab.vue'
import StrategicSuitabilityTab from './tabs/StrategicSuitabilityTab.vue'
import PreliminaryFeasibilityTab from './tabs/PreliminaryFeasibilityTab.vue'
import RiskAssessmentTab from './tabs/RiskAssessmentTab.vue'
import PPPSuitabilityTab from './tabs/PPPSuitabilityTab.vue'
import FiscalAffordabilityTab from './tabs/FiscalAffordabilityTab.vue'
import InstitutionalCapabilityTab from './tabs/InstitutionalCapabilityTab.vue'
import ClimateRiskTab from './tabs/ClimateRiskTab.vue'
import ClimateSmartPPPTab from './tabs/ClimateSmartPPPTab.vue'

export default {
  name: 'ProjectCreation',
  components: {
    BasicProjectDataTab,
    StrategicSuitabilityTab,
    PreliminaryFeasibilityTab,
    RiskAssessmentTab,
    PPPSuitabilityTab,
    FiscalAffordabilityTab,
    InstitutionalCapabilityTab,
    ClimateRiskTab,
    ClimateSmartPPPTab
  },
  props: {
    editProjectId: {
      type: String,
      default: null
    },
    viewMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentTab: 'basic-data',
      hasChanges: false,
      isEditMode: !!this.editProjectId,
      tabs: [
        {
          id: 'basic-data',
          name: 'Basic Project Data',
          description: 'Fundamental project information and profile',
          completed: false,
          hasData: false,
          accessible: true,
          required: true
        },
        {
          id: 'strategic-suitability',
          name: 'Strategic Suitability',
          description: 'Alignment with strategic objectives and priorities',
          completed: false,
          hasData: false,
          accessible: false,
          required: false
        },
        {
          id: 'preliminary-feasibility',
          name: 'Preliminary Feasibility',
          description: 'Technical and economic viability assessment',
          completed: false,
          hasData: false,
          accessible: false,
          required: false
        },
        {
          id: 'risk-assessment',
          name: 'Risk Assessment',
          description: 'Comprehensive risk identification and evaluation',
          completed: false,
          hasData: false,
          accessible: false,
          required: false
        },
        {
          id: 'ppp-suitability',
          name: 'PPP Suitability',
          description: 'Public-private partnership viability analysis',
          completed: false,
          hasData: false,
          accessible: false,
          required: false
        },
        {
          id: 'fiscal-affordability',
          name: 'Fiscal Affordability',
          description: 'Financial sustainability and budget impact',
          completed: false,
          hasData: false,
          accessible: false,
          required: false
        },
        {
          id: 'institutional-capability',
          name: 'Institutional Capability',
          description: 'Organizational readiness and capacity assessment',
          completed: false,
          hasData: false,
          accessible: false,
          required: false
        },
        {
          id: 'climate-risk',
          name: 'Climate Risk Exposure',
          description: 'Environmental impact and climate vulnerability',
          completed: false,
          hasData: false,
          accessible: false,
          required: false
        },
        {
          id: 'climate-smart-ppp',
          name: 'Climate-Smart PPP',
          description: 'Sustainable development and climate resilience',
          completed: false,
          hasData: false,
          accessible: false,
          required: false
        }
      ],
      projectData: {
        basicData: {},
        strategicSuitability: {},
        preliminaryFeasibility: {},
        riskAssessment: {},
        pppSuitability: {},
        fiscalAffordability: {},
        institutionalCapability: {},
        climateRisk: {},
        climateSmartPPP: {}
      },
      tabValidation: {}
    }
  },
  computed: {
    currentTabIndex() {
      return this.tabs.findIndex(tab => tab.id === this.currentTab)
    },
    currentTabObject() {
      return this.tabs[this.currentTabIndex]
    },
    canGoPrevious() {
      return this.currentTabIndex > 0
    },
    canGoNext() {
      return this.currentTabIndex < this.tabs.length - 1 && this.tabValidation[this.currentTab]
    },
    isLastTab() {
      return this.currentTabIndex === this.tabs.length - 1
    },
    canComplete() {
      return this.tabs.filter(tab => tab.required).every(tab => tab.completed)
    },
    progressPercentage() {
      const completedTabs = this.tabs.filter(tab => tab.completed).length
      return (completedTabs / this.tabs.length) * 100
    }
  },
  methods: {
    generateRandomData(existingProjectName = null, existingProjectData = null) {
      // Generate random data for all dimensions
      this.projectData = {
        basicData: this.generateBasicData(existingProjectName, existingProjectData),
        strategicSuitability: this.generateStrategicSuitability(),
        preliminaryFeasibility: this.generatePreliminaryFeasibility(),
        riskAssessment: this.generateRiskAssessment(),
        pppSuitability: this.generatePPPSuitability(),
        fiscalAffordability: this.generateFiscalAffordability(),
        institutionalCapability: this.generateInstitutionalCapability(),
        climateRisk: this.generateClimateRisk(),
        climateSmartPPP: this.generateClimateSmartPPP()
      }
      
      // Mark all tabs as completed and accessible
      this.tabs.forEach(tab => {
        tab.completed = true
        tab.hasData = true
        tab.accessible = true
      })
      
      // Update validation for all tabs
      this.tabs.forEach(tab => {
        this.tabValidation[tab.id] = true
      })
    },

    generateBasicData(existingProjectName = null, existingProjectData = null) {
      // If we have existing project data, use it
      if (existingProjectData) {
        return {
          projectName: existingProjectData.name,
          implementingAgency: existingProjectData.implementingAgency,
          sector: existingProjectData.sector,
          stage: existingProjectData.stage,
          totalCost: existingProjectData.totalCost,
          description: existingProjectData.description,
          beneficiaries: Math.floor(Math.random() * 1000000) + 100000, // 100k-1.1M
          timeline: existingProjectData.timeline,
          fundingSource: existingProjectData.fundingSource
        }
      }
      
      // Otherwise generate random data
      const projects = [
        'Highway Infrastructure Development Project',
        'Urban Water Supply System',
        'Renewable Energy Power Plant',
        'Public Hospital Modernization',
        'Smart City Transportation Hub',
        'Waste Management Facility',
        'Educational Technology Center',
        'Port Development Project'
      ]
      const agencies = [
        'Ministry of Infrastructure',
        'Department of Public Works',
        'Ministry of Health',
        'Department of Transportation',
        'Ministry of Education',
        'Environmental Protection Agency'
      ]
      const sectors = ['Transportation', 'Energy', 'Water', 'Health', 'Education', 'Environment', 'ICT']
      const stages = ['Concept', 'Pre-feasibility', 'Feasibility', 'Procurement', 'Implementation']
      
      return {
        projectName: existingProjectName || projects[Math.floor(Math.random() * projects.length)],
        implementingAgency: agencies[Math.floor(Math.random() * agencies.length)],
        sector: sectors[Math.floor(Math.random() * sectors.length)],
        stage: stages[Math.floor(Math.random() * stages.length)],
        totalCost: Math.floor(Math.random() * 500) + 50, // 50-550 million
        description: 'This is a comprehensive infrastructure development project aimed at improving public services and economic growth in the region.',
        beneficiaries: Math.floor(Math.random() * 1000000) + 100000, // 100k-1.1M
        timeline: Math.floor(Math.random() * 60) + 12, // 12-72 months
        fundingSource: ['Government Budget', 'International Loan', 'PPP', 'Grant'][Math.floor(Math.random() * 4)]
      }
    },

    generateStrategicSuitability() {
      return {
        strategicImportance: ['yes', 'no'][Math.floor(Math.random() * 2)],
        policyAlignment: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        nationalPriority: ['yes', 'no'][Math.floor(Math.random() * 2)],
        economicImpact: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        socialBenefit: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        urgency: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)]
      }
    },

    generatePreliminaryFeasibility() {
      return {
        technicalFeasibility: ['feasible', 'challenging', 'not-feasible'][Math.floor(Math.random() * 3)],
        financialViability: ['viable', 'marginal', 'not-viable'][Math.floor(Math.random() * 3)],
        marketDemand: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        competitiveAdvantage: ['strong', 'moderate', 'weak'][Math.floor(Math.random() * 3)],
        irr: Math.floor(Math.random() * 20) + 5, // 5-25%
        npv: Math.floor(Math.random() * 100) + 10, // 10-110 million
        paybackPeriod: Math.floor(Math.random() * 15) + 3 // 3-18 years
      }
    },

    generateRiskAssessment() {
      return {
        constructionRisk: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        operationalRisk: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        financialRisk: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        politicalRisk: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        environmentalRisk: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        regulatoryRisk: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        overallRiskLevel: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        mitigation: 'Comprehensive risk mitigation strategies have been developed including insurance, contingency planning, and stakeholder engagement.'
      }
    },

    generatePPPSuitability() {
      return {
        pppStructure: ['concession', 'joint-venture', 'management-contract', 'lease'][Math.floor(Math.random() * 4)],
        privatePartnerCapability: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        marketInterest: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        revenueModel: ['user-fees', 'government-payment', 'hybrid'][Math.floor(Math.random() * 3)],
        contractDuration: Math.floor(Math.random() * 20) + 10, // 10-30 years
        riskTransfer: ['optimal', 'adequate', 'inadequate'][Math.floor(Math.random() * 3)]
      }
    },

    generateFiscalAffordability() {
      return {
        budgetAvailability: ['adequate', 'limited', 'insufficient'][Math.floor(Math.random() * 3)],
        debtCapacity: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        fiscalImpact: Math.floor(Math.random() * 10) + 1, // 1-11% of budget
        sustainabilityRating: ['sustainable', 'marginal', 'unsustainable'][Math.floor(Math.random() * 3)],
        contingentLiabilities: Math.floor(Math.random() * 50) + 10, // 10-60 million
        affordabilityIndex: Math.random() * 5 + 1 // 1-6 scale
      }
    },

    generateInstitutionalCapability() {
      return {
        managementCapacity: ['strong', 'adequate', 'weak'][Math.floor(Math.random() * 3)],
        technicalExpertise: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        procurementExperience: ['extensive', 'moderate', 'limited'][Math.floor(Math.random() * 3)],
        regulatoryFramework: ['comprehensive', 'adequate', 'insufficient'][Math.floor(Math.random() * 3)],
        stakeholderSupport: ['strong', 'moderate', 'weak'][Math.floor(Math.random() * 3)],
        capacityBuildingNeed: ['minimal', 'moderate', 'extensive'][Math.floor(Math.random() * 3)]
      }
    },

    generateClimateRisk() {
      return {
        physicalRisk: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        transitionRisk: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        adaptationMeasures: ['comprehensive', 'partial', 'minimal'][Math.floor(Math.random() * 3)],
        resilience: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        carbonFootprint: Math.floor(Math.random() * 1000) + 100, // 100-1100 tons CO2
        climateImpactScore: Math.floor(Math.random() * 10) + 1 // 1-10 scale
      }
    },

    generateClimateSmartPPP() {
      return {
        greenInnovation: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
        sustainabilityMetrics: ['comprehensive', 'adequate', 'basic'][Math.floor(Math.random() * 3)],
        environmentalCompliance: ['full', 'partial', 'minimal'][Math.floor(Math.random() * 3)],
        climateFinancing: ['available', 'limited', 'unavailable'][Math.floor(Math.random() * 3)],
        sdgAlignment: Math.floor(Math.random() * 17) + 1, // SDG 1-17
        sustainabilityRating: ['A', 'B', 'C'][Math.floor(Math.random() * 3)]
      }
    },

    selectTab(tabId) {
      const tab = this.tabs.find(t => t.id === tabId)
      if (tab && tab.accessible) {
        this.currentTab = tabId
      }
    },
    updateTabData(data) {
      const tabKey = this.currentTab.replace(/-([a-z])/g, (g) => g[1].toUpperCase())
      this.projectData[tabKey] = { ...this.projectData[tabKey], ...data }
      this.hasChanges = true
      
      // Update tab status
      const currentTab = this.tabs.find(tab => tab.id === this.currentTab)
      if (currentTab) {
        currentTab.hasData = Object.keys(data).length > 0
      }
    },
    validateTab(isValid) {
      this.tabValidation[this.currentTab] = isValid
      
      // Update tab completion status
      const currentTab = this.tabs.find(tab => tab.id === this.currentTab)
      if (currentTab) {
        currentTab.completed = isValid
        
        // Make next tab accessible if current is completed
        const nextTabIndex = this.currentTabIndex + 1
        if (isValid && nextTabIndex < this.tabs.length) {
          this.tabs[nextTabIndex].accessible = true
        }
      }
    },
    nextTab() {
      if (this.canGoNext) {
        const nextIndex = this.currentTabIndex + 1
        this.currentTab = this.tabs[nextIndex].id
      }
    },
    previousTab() {
      if (this.canGoPrevious) {
        const prevIndex = this.currentTabIndex - 1
        this.currentTab = this.tabs[prevIndex].id
      }
    },
    saveDraft() {
      // Save current progress
      const projectDraft = {
        id: this.isEditMode ? this.editProjectId : Date.now().toString(),
        ...this.projectData,
        lastSaved: new Date().toISOString(),
        status: 'draft',
        completedTabs: this.tabs.filter(tab => tab.completed).map(tab => tab.id)
      }
      
      // Store in localStorage (in real app, send to backend)
      localStorage.setItem(`project_draft_${projectDraft.id}`, JSON.stringify(projectDraft))
      
      // If in edit mode, ensure we also remove any old storage keys that might exist
      if (this.isEditMode) {
        // Check for other possible storage keys and remove them to maintain consistency
        const alternativeKeys = [
          `completed_project_${this.editProjectId}`,
          `psat_project_${this.editProjectId}`
        ]
        alternativeKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key)
          }
        })
      }
      
      this.hasChanges = false
      
      // Show success message
      alert('Project saved as draft successfully!')
    },
    completeAssessment() {
      if (this.canComplete) {
        const projectData = {
          ...this.projectData,
          completedAt: new Date().toISOString(),
          status: 'completed'
        }
        
        this.$emit('assessment-complete', projectData)
      }
    },
    goBack() {
      if (this.hasChanges) {
        if (confirm('You have unsaved changes. Do you want to save before leaving?')) {
          this.saveDraft()
        }
      }
      this.$emit('navigate', 'dashboard')
    },
    navigateToHome() {
      this.$emit('navigate', 'landing')
    }
  },
  mounted() {
    // Generate random data if in view mode
    if (this.viewMode) {
      // Get the project data from the hardcoded projects list
      let existingProjectData = null
      if (this.editProjectId) {
        // Hardcoded projects array (same as in ProjectList and NavigationDashboard)
        const hardcodedProjects = [
          {
            id: 1,
            name: 'Metro Line Extension Project',
            sector: 'Transport',
            stage: 'Implementation',
            totalCost: 450,
            implementingAgency: 'Department of Transportation',
            timeline: 48,
            fundingSource: 'PPP',
            description: 'Comprehensive metro line extension to improve urban transportation infrastructure and reduce traffic congestion.'
          },
          {
            id: 2,
            name: 'Water Treatment Facility',
            sector: 'Water',
            stage: 'Feasibility',
            totalCost: 280,
            implementingAgency: 'Ministry of Water Resources',
            timeline: 36,
            fundingSource: 'Government Budget',
            description: 'Modern water treatment facility to provide clean drinking water to urban and rural communities.'
          },
          {
            id: 3,
            name: 'Solar Power Plant',
            sector: 'Energy',
            stage: 'Concept',
            totalCost: 320,
            implementingAgency: 'Ministry of Energy',
            timeline: 42,
            fundingSource: 'International Loan',
            description: 'Large-scale solar power generation facility to support renewable energy transition and reduce carbon emissions.'
          },
          {
            id: 4,
            name: 'Hospital Infrastructure',
            sector: 'Health',
            stage: 'Pre-feasibility',
            totalCost: 180,
            implementingAgency: 'Ministry of Health',
            timeline: 30,
            fundingSource: 'Grant',
            description: 'State-of-the-art hospital infrastructure to improve healthcare services and medical emergency response capabilities.'
          },
          {
            id: 5,
            name: 'Smart City Initiative',
            sector: 'Urban infrastructure',
            stage: 'Procurement',
            totalCost: 520,
            implementingAgency: 'Ministry of Urban Development',
            timeline: 60,
            fundingSource: 'PPP',
            description: 'Integrated smart city development with IoT infrastructure, digital governance, and sustainable urban planning.'
          }
        ]
        
        existingProjectData = hardcodedProjects.find(p => p.id == this.editProjectId)
      }
      
      this.generateRandomData(existingProjectData?.name || 'Sample Project Assessment', existingProjectData)
      return
    }
    
    // Load existing project data if in edit mode
    if (this.isEditMode) {
      // Try different storage key formats to find the project data
      let projectData = null
      const possibleKeys = [
        `project_draft_${this.editProjectId}`,
        `completed_project_${this.editProjectId}`,
        `psat_project_${this.editProjectId}`
      ]
      
      for (const key of possibleKeys) {
        const savedProject = localStorage.getItem(key)
        if (savedProject) {
          try {
            projectData = JSON.parse(savedProject)
            break
          } catch (error) {
            console.error('Error parsing project data:', error)
          }
        }
      }
      
      if (projectData) {
        // Ensure the data structure is compatible with the form
        this.projectData = {
          basicData: projectData.basicData || {},
          strategicSuitability: projectData.strategicSuitability || {},
          preliminaryFeasibility: projectData.preliminaryFeasibility || {},
          riskAssessment: projectData.riskAssessment || {},
          pppSuitability: projectData.pppSuitability || {},
          fiscalAffordability: projectData.fiscalAffordability || {},
          institutionalCapability: projectData.institutionalCapability || {},
          climateRisk: projectData.climateRisk || {},
          climateSmartPPP: projectData.climateSmartPPP || {}
        }
        
        // Update tab statuses based on saved data
        if (projectData.completedTabs) {
          projectData.completedTabs.forEach(tabId => {
            const tab = this.tabs.find(t => t.id === tabId)
            if (tab) {
              tab.completed = true
              tab.hasData = true
              tab.accessible = true
            }
          })
          
          // Make next tab accessible
          const lastCompletedIndex = this.tabs.findIndex(tab => 
            projectData.completedTabs.includes(tab.id)
          )
          if (lastCompletedIndex >= 0 && lastCompletedIndex < this.tabs.length - 1) {
            this.tabs[lastCompletedIndex + 1].accessible = true
          }
        }
      } else {
        // No saved data found, generate the same data that would be shown in view mode
        // Get the project data from the hardcoded projects list
        let existingProjectData = null
        if (this.editProjectId) {
          // Hardcoded projects array (same as in ProjectList and NavigationDashboard)
          const hardcodedProjects = [
            {
              id: 1,
              name: 'Metro Line Extension Project',
              sector: 'Transport',
              stage: 'Implementation',
              totalCost: 450,
              implementingAgency: 'Department of Transportation',
              timeline: 48,
              fundingSource: 'PPP',
              description: 'Comprehensive metro line extension to improve urban transportation infrastructure and reduce traffic congestion.'
            },
            {
              id: 2,
              name: 'Water Treatment Facility',
              sector: 'Water',
              stage: 'Feasibility',
              totalCost: 280,
              implementingAgency: 'Ministry of Water Resources',
              timeline: 36,
              fundingSource: 'Government Budget',
              description: 'Modern water treatment facility to provide clean drinking water to urban and rural communities.'
            },
            {
              id: 3,
              name: 'Solar Power Plant',
              sector: 'Energy',
              stage: 'Concept',
              totalCost: 320,
              implementingAgency: 'Ministry of Energy',
              timeline: 42,
              fundingSource: 'International Loan',
              description: 'Large-scale solar power generation facility to support renewable energy transition and reduce carbon emissions.'
            },
            {
              id: 4,
              name: 'Hospital Infrastructure',
              sector: 'Health',
              stage: 'Pre-feasibility',
              totalCost: 180,
              implementingAgency: 'Ministry of Health',
              timeline: 30,
              fundingSource: 'Grant',
              description: 'State-of-the-art hospital infrastructure to improve healthcare services and medical emergency response capabilities.'
            },
            {
              id: 5,
              name: 'Smart City Initiative',
              sector: 'Urban infrastructure',
              stage: 'Procurement',
              totalCost: 520,
              implementingAgency: 'Ministry of Urban Development',
              timeline: 60,
              fundingSource: 'PPP',
              description: 'Integrated smart city development with IoT infrastructure, digital governance, and sustainable urban planning.'
            }
          ]
          
          existingProjectData = hardcodedProjects.find(p => p.id == this.editProjectId)
        }
        
        this.generateRandomData(existingProjectData?.name || 'Sample Project Assessment', existingProjectData)
      }
    }
  }
}
</script>

<style scoped>
.project-creation {
  min-height: 100vh;
  background: #f8f9fa;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Header */
.creation-header {
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.kpmg-logo {
  background: #00338d;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: bold;
  font-size: 1.2rem;
  letter-spacing: 2px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
}

.breadcrumb-link {
  color: #00338d;
  cursor: pointer;
  transition: color 0.3s;
}

.breadcrumb-link:hover {
  color: #002a75;
}

.breadcrumb-separator {
  color: #ccc;
}

.breadcrumb-current {
  font-weight: 600;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.save-draft-btn {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.save-draft-btn:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-1px);
}

.save-draft-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.cancel-btn {
  background: transparent;
  color: #6c757d;
  border: 1px solid #6c757d;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.cancel-btn:hover {
  background: #6c757d;
  color: white;
}

/* Tabs Section */
.tabs-section {
  background: white;
  padding: 2rem 0;
  border-bottom: 1px solid #e9ecef;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.tabs-header {
  text-align: center;
  margin-bottom: 2rem;
}

.tabs-header h1 {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.tabs-header p {
  color: #666;
  font-size: 1.1rem;
}

.tabs-navigation {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.tab-item {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;
  opacity: 0.6;
}

.tab-item.accessible {
  opacity: 1;
}

.tab-item.active {
  background: #00338d;
  color: white;
  border-color: #00338d;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,51,141,0.3);
}

.tab-item.completed {
  border-color: #28a745;
  background: #d4edda;
}

.tab-item.completed.active {
  background: #00338d;
  color: white;
}

.tab-item:hover.accessible {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.tab-number {
  background: #dee2e6;
  color: #495057;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.tab-item.active .tab-number {
  background: white;
  color: #00338d;
}

.tab-item.completed .tab-number {
  background: #28a745;
  color: white;
}

.tab-content {
  flex: 1;
}

.tab-content h3 {
  font-size: 1.1rem;
  margin: 0 0 0.25rem 0;
  font-weight: 600;
}

.tab-content p {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.8;
}

.tab-status {
  flex-shrink: 0;
}

.status-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
}

.status-icon.completed {
  background: #28a745;
  color: white;
}

.status-icon.partial {
  background: #ffc107;
  color: white;
}

.status-icon.empty {
  background: #dee2e6;
  color: #6c757d;
}

/* Content Section */
.content-section {
  padding: 2rem 0;
}

.content-container {
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  margin-bottom: 2rem;
  min-height: 500px;
}

/* Navigation Footer */
.navigation-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
}

.nav-button {
  padding: 1rem 2rem;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 120px;
}

.nav-button.primary {
  background: #00338d;
  color: white;
}

.nav-button.primary:hover:not(:disabled) {
  background: #002a75;
  transform: translateY(-1px);
}

.nav-button.primary.complete {
  background: #28a745;
}

.nav-button.primary.complete:hover:not(:disabled) {
  background: #218838;
}

.nav-button.secondary {
  background: transparent;
  color: #6c757d;
  border: 1px solid #6c757d;
}

.nav-button.secondary:hover:not(:disabled) {
  background: #6c757d;
  color: white;
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.tab-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.tab-progress span {
  color: #666;
  font-weight: 500;
}

.progress-bar {
  width: 200px;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00338d, #28a745);
  transition: width 0.3s ease;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .tabs-navigation {
    grid-template-columns: 1fr;
  }
  
  .navigation-footer {
    flex-direction: column;
    gap: 1rem;
  }
  
  .nav-button {
    width: 100%;
  }
}
</style>